import { type HijriDisplayMode, hijriDate, MONEY_SCALE, minutesToHours } from '@sahab/contracts'

export type Language = 'en' | 'ar'

export const TIME_ZONE = 'Asia/Muscat'

/**
 * Latin digits in both languages (Omani business practice for amounts and codes); the rest of the
 * locale (month names, order) follows the UI language.
 */
export function localeTag(language: Language): string {
  return language === 'ar' ? 'ar-OM-u-nu-latn' : 'en-GB-u-nu-latn'
}

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>()

function dateFormat(language: Language, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d|${language}|${JSON.stringify(options)}`
  let fmt = cache.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(localeTag(language), { timeZone: TIME_ZONE, ...options })
    cache.set(key, fmt)
  }
  return fmt as Intl.DateTimeFormat
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * English dates read `24 Sep 2026` (day-month-year, as in Oman). en-GB spells September "Sept" in
 * recent ICU data, so the parts are assembled from en-US month names in day-first order.
 */
function englishDate(date: Date, withTime: boolean): string {
  const key = `d|en-parts|${withTime}`
  let fmt = cache.get(key) as Intl.DateTimeFormat | undefined
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US-u-nu-latn', {
      timeZone: TIME_ZONE,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      ...(withTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
    })
    cache.set(key, fmt)
  }
  const parts = fmt.formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  const day = `${part('day')} ${part('month')} ${part('year')}`
  return withTime ? `${day}, ${part('hour')}:${part('minute')}` : day
}

/**
 * The currency as text in the UI language: "OMR" in English, «ر.ع.» in Arabic. Only where the rial sign
 * (an element: `CurrencyLabel`, `Money`) can't go: accessible names, screen-reader text, plain-text
 * notifications. Other labels pass through.
 */
export function currencyLabel(code: string, language: Language): string {
  return code === 'OMR' && language === 'ar' ? 'ر.ع.' : code
}

export function formatDate(iso: string | null | undefined, language: Language): string {
  const date = toDate(iso)
  if (!date) return ''
  if (language === 'en') return englishDate(date, false)
  return dateFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }).format(date)
}

export function formatDateTime(iso: string | null | undefined, language: Language): string {
  const date = toDate(iso)
  if (!date) return ''
  if (language === 'en') return englishDate(date, true)
  return dateFormat(language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
}

/**
 * The Umm al-Qura Hijri date of a date's Muscat day (ASM-135): `Ramadan 1, 1447 AH` / `1 رمضان 1447 هـ`.
 */
export function formatHijriDate(iso: string | null | undefined, language: Language): string {
  const date = toDate(iso)
  if (!date) return ''
  return hijriDate(date, language)
}

/**
 * A document date as the `documents` setting `showHijri` asks: the Gregorian date alone, or followed by
 * the Hijri one (`25 Sep 2026 · Rabiʻ II 14, 1448 AH`). `legal` adds it only on legal documents.
 */
export function formatDateWithHijri(
  iso: string | null | undefined,
  language: Language,
  mode: HijriDisplayMode,
  legalDocument = false,
): string {
  const gregorian = formatDate(iso, language)
  if (!gregorian || mode === 'never' || (mode === 'legal' && !legalDocument)) return gregorian
  return `${gregorian} · ${formatHijriDate(iso, language)}`
}

/** The date on a stamp: `24 SEP 2026` in English, `24 سبتمبر 2026` in Arabic (never uppercased). */
export function formatStampDate(iso: string, language: Language): string {
  const date = toDate(iso)
  if (!date) return ''
  return language === 'ar' ? formatDate(iso, 'ar') : englishDate(date, false).toUpperCase()
}

/**
 * A calendar month (`YYYY-MM`): `September 2026` / `سبتمبر 2026`, or `Sep 2026` with `short`.
 * Short English month names come from en-US for the same reason as `englishDate` ("Sept").
 */
export function formatMonth(month: string, language: Language, width: 'long' | 'short' = 'long'): string {
  const match = /^(\d{4})-(\d{2})$/.exec(month)
  if (!match) return month
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 15))
  if (language === 'en') {
    const key = `d|en-month|${width}`
    let fmt = cache.get(key) as Intl.DateTimeFormat | undefined
    if (!fmt) {
      fmt = new Intl.DateTimeFormat('en-US-u-nu-latn', { timeZone: 'UTC', month: width, year: 'numeric' })
      cache.set(key, fmt)
    }
    const parts = fmt.formatToParts(date)
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
    return `${part('month')} ${part('year')}`
  }
  // Mid-month: the same month in Muscat as in UTC.
  return dateFormat(language, { month: width, year: 'numeric' }).format(date)
}

/**
 * A business date (`YYYY-MM-DD`) without its year, for a week inside the current year: `4 Oct` /
 * `4 أكتوبر`; `withYear` adds it (`4 Oct 2025`). English month names from en-US, as `englishDate`.
 */
export function formatDayMonth(iso: string, language: Language, withYear = false): string {
  const date = toDate(`${iso.slice(0, 10)}T12:00:00Z`)
  if (!date) return ''
  if (language === 'en') {
    const full = englishDate(date, false)
    return withYear ? full : full.replace(/ \d{4}$/, '')
  }
  return dateFormat(language, {
    day: 'numeric',
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(date)
}

/** The weekday of a business date (`YYYY-MM-DD`): `Sat` / `السبت`, or `Saturday` with `long`. */
export function formatWeekday(iso: string, language: Language, width: 'short' | 'long' = 'short'): string {
  const date = toDate(`${iso}T12:00:00Z`)
  if (!date) return ''
  return dateFormat(language, { weekday: width }).format(date)
}

/**
 * A day as week names say it (P7, ASM-216; weeks run Sunday to Saturday): `Sun 4 Oct` / «الأحد 4 أكتوبر».
 * The year shows when `withYear` says so, else only when the day falls outside `today`'s year (Muscat).
 * The one helper behind "Week of Sun 4 Oct", "the record of Sat 10 Oct" and a report's week.
 */
export function formatWeekDay(
  iso: string,
  language: Language,
  withYear?: boolean,
  today: Date | string = new Date(),
): string {
  const year =
    typeof today === 'string' ? today.slice(0, 4) : dateFormat('en', { year: 'numeric' }).format(today)
  const showYear = withYear ?? iso.slice(0, 4) !== year
  return `${formatWeekday(iso.slice(0, 10), language)} ${formatDayMonth(iso, language, showYear)}`
}

/**
 * A week's first and last day for a range ("Sun 4 Oct to Sat 10 Oct 2026" on paper): the last day
 * always with its year, the first with its year too when the week spans two years ("Sun 27 Dec 2026 to
 * Sat 2 Jan 2027"), so neither end is read in the wrong year.
 */
export function formatWeekSpan(from: string, to: string, language: Language): { from: string; to: string } {
  return {
    from: formatWeekDay(from, language, from.slice(0, 4) !== to.slice(0, 4)),
    to: formatWeekDay(to, language, true),
  }
}

/** Hour of day in Muscat, for the greeting. */
export function muscatHour(now: Date = new Date()): number {
  const hour = dateFormat('en', { hour: 'numeric', hourCycle: 'h23' }).format(now)
  return Number.parseInt(hour, 10)
}

export function formatCount(value: number, language: Language): string {
  const key = `n|${language}`
  let fmt = cache.get(key)
  if (!fmt) {
    fmt = new Intl.NumberFormat(localeTag(language))
    cache.set(key, fmt)
  }
  return (fmt as Intl.NumberFormat).format(value)
}

/**
 * A whole percentage as one token, "81%", the sign against the digits in both languages (Latin digits),
 * so every screen spaces it the same.
 */
export function formatPercent(value: number, language: Language): string {
  return `${formatCount(Math.round(value), language)}%`
}

/**
 * Minutes as decimal hours, for tables, grid cells, totals and badges (P6 UX decision 43): up to two
 * decimals, grouped, Latin digits in both languages: `7.5`, `37.25`, `1,240.33`. Never negative.
 */
export function formatHours(minutes: number): string {
  const hours = minutesToHours(Math.max(0, Math.round(minutes)))
  const [whole = '0', fraction] = hours.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return fraction ? `${grouped}.${fraction}` : grouped
}

/**
 * Minutes as a duration in a sentence (a title, a toast, a notification): `7 h 30 min`, `8 h`, `45 min`;
 * «7 س 30 د» in Arabic. Never clock-like (`7:30`), which reads as a time of day. Never negative.
 */
export function formatDuration(minutes: number, language: Language): string {
  const total = Math.max(0, Math.round(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  const [hour, minute] = language === 'ar' ? ['س', 'د'] : ['h', 'min']
  const hours = `${h.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${hour}`
  if (m === 0) return hours
  return h === 0 ? `${m} ${minute}` : `${hours} ${m} ${minute}`
}

export interface FormattedAmount {
  /** Grouped, fixed-scale text with an ASCII minus for negatives (copies cleanly into spreadsheets). */
  text: string
  negative: boolean
  valid: boolean
}

const DECIMAL = /^([+-])?(\d+)(?:\.(\d*))?$/

/**
 * Formats a decimal string without ever converting it to a binary float: amounts travel as strings
 * (see @sahab/contracts moneySchema). Extra fraction digits are rounded half away from zero.
 */
export function formatAmount(value: string, scale: number = MONEY_SCALE): FormattedAmount {
  const match = DECIMAL.exec(value.trim())
  if (!match) return { text: value, negative: false, valid: false }
  const sign = match[1] === '-' ? -1 : 1
  let whole = match[2] ?? '0'
  let fraction = match[3] ?? ''

  if (fraction.length > scale) {
    const excess = fraction.length - scale
    const divisor = 10n ** BigInt(excess)
    const scaled = BigInt(whole + fraction)
    let rounded = scaled / divisor
    if ((scaled % divisor) * 2n >= divisor) rounded += 1n
    const digits = rounded.toString().padStart(scale + 1, '0')
    whole = scale === 0 ? digits : digits.slice(0, -scale)
    fraction = scale === 0 ? '' : digits.slice(-scale)
  } else {
    fraction = fraction.padEnd(scale, '0')
  }

  whole = whole.replace(/^0+(?=\d)/, '')
  const isZero = /^0*$/.test(whole + fraction)
  const negative = sign < 0 && !isZero
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const text = `${negative ? '-' : ''}${grouped}${scale > 0 ? `.${fraction}` : ''}`
  return { text, negative, valid: true }
}

export interface BilingualName {
  name: string
  nameAr: string | null
}

/** Bilingual records show the name in the UI language, falling back to the other. */
export function displayName(record: BilingualName, language: Language): string {
  if (language === 'ar') return record.nameAr?.trim() || record.name
  return record.name || record.nameAr || ''
}

/** The name in the other language, when it exists and differs — shown as a secondary line. */
export function secondaryName(record: BilingualName, language: Language): string | null {
  const other = language === 'ar' ? record.name : record.nameAr
  const primary = displayName(record, language)
  return other && other !== primary ? other : null
}
